import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePlanLimits } from '@/hooks/useOrganization';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getLimitStatus, detectThresholdCrossing } from '@/lib/plan-limits';

interface PlanLimitBannerProps {
  tipo: string;
  label: string;
}

// Module-scope dedup: fire each threshold at most once per resource per session.
// Cleared on logout via resetPlanLimitTracking() — called from AuthContext.
const firedThresholds = new Set<string>();

export function resetPlanLimitTracking() {
  firedThresholds.clear();
}

export function PlanLimitBanner({ tipo, label }: PlanLimitBannerProps) {
  const { t } = useTranslation();
  const { data } = usePlanLimits(tipo);
  const { track } = useAnalytics();
  const previousRatio = useRef<number | null>(null);

  useEffect(() => {
    if (!data || data.limite === null) return;
    const ratio = data.uso_atual / data.limite;
    const { soft, hard } = detectThresholdCrossing(previousRatio.current, ratio);
    const softKey = `${tipo}:soft`;
    const hardKey = `${tipo}:hard`;

    if (soft && !firedThresholds.has(softKey)) {
      track('plan_limit_hit_soft', { resource: tipo, current: data.uso_atual, max: data.limite });
      firedThresholds.add(softKey);
    }
    if (hard && !firedThresholds.has(hardKey)) {
      track('plan_limit_hit_hard', { resource: tipo, current: data.uso_atual, max: data.limite });
      firedThresholds.add(hardKey);
    }

    previousRatio.current = ratio;
  }, [data, tipo, track]);

  if (!data) return null;
  const status = getLimitStatus(data.uso_atual, data.limite);
  if (status === 'none') return null;

  if (status === 'hard') {
    return (
      <Alert variant="destructive" className="mb-4">
        <XCircle className="h-4 w-4" />
        <AlertDescription>
          {t('billing.limits.reached', { label, current: data.uso_atual, limit: data.limite })}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-4 border-yellow-400 bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700">
      <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
      <AlertDescription>
        {t('billing.limits.approaching', { current: data.uso_atual, limit: data.limite, label })}
      </AlertDescription>
    </Alert>
  );
}
